import React, { forwardRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import * as Yup from "yup";

import { FormControl, TextField, Typography } from "@mui/material";
import { ButtonsWrapper, FormRow, StyledForm } from "./styled";

import { getRequiredField } from "helpers/formValidations";
import { FACILITIES_CONFIG, FACILITIES_PHRASE } from "./config";

import { MapComponent } from "components/MapComponent/MapComponent";

import { fetchNewFacility, fetchEditFacility } from "redux/actions/buildings/facilities/facilitiesActions";

import AutocompleteControlled from "components/AutocompleteControlled/AutocompleteControlled";
import RegularButton from "components/CustomButtons/Button";
import { editCollectPeriod } from "redux/actions/buildings/collectPeriods/collectPeriodsActions";

const FacilitiesSchema = Yup.object({
  [FACILITIES_CONFIG.registrationDate.key]: Yup.date("")
    .typeError(getRequiredField(FACILITIES_CONFIG.registrationDate.label))
    .required(getRequiredField(FACILITIES_CONFIG.registrationDate.label)),
  [FACILITIES_CONFIG.facilityName.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.facilityName.label)
  ),
  [FACILITIES_CONFIG.contactName.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.contactName.label)
  ),
  [FACILITIES_CONFIG.contactNumber.key]: Yup.string()
    .required(getRequiredField(FACILITIES_CONFIG.contactNumber.label))
    .max(10)
    .min(10),
  [FACILITIES_CONFIG.regimen.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.regimen.label)
  ),
  [FACILITIES_CONFIG.route.key]: Yup.object().required(
    getRequiredField(FACILITIES_CONFIG.route.label)
  ),

  [FACILITIES_CONFIG.street.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.street.label)
  ),
  [FACILITIES_CONFIG.extNumber.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.extNumber.label)
  ),
  [FACILITIES_CONFIG.intNumber.key]: Yup.string(),
  [FACILITIES_CONFIG.postalCode.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.postalCode.label)
  ),
  [FACILITIES_CONFIG.colony.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.colony.label)
  ),
  [FACILITIES_CONFIG.municipality.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.municipality.label)
  ),
  [FACILITIES_CONFIG.state.key]: Yup.string().required(
    getRequiredField(FACILITIES_CONFIG.state.label)
  ),
  [FACILITIES_CONFIG.reference.key]: Yup.string(),
  [FACILITIES_CONFIG.paymentCycle.key]: Yup.object().required(
    getRequiredField(FACILITIES_CONFIG.paymentCycle.label)
  )
});

export const FacilitiesForm = forwardRef(props => {
  const dispatch = useDispatch();
  const { handleClose, stationSelected } = props;
  const { isAddMode, deleteMode, editFacility } = useSelector(
    state => state.facilities
  );

  const { collectPeriodList } = useSelector(state => state.collectPeriods);
  const { routeList } = useSelector(state => state.routesBuildings);

  const {
    reset,
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(FacilitiesSchema),
    defaultValues: {
      [FACILITIES_CONFIG.regimen.key]: "CONDOMINIO"
    }
  });
  const [location, setLocation] = useState({
    latitude: 0,
    longitude: 0
  });

  const handleCloseForm = () => {
    handleClose();
    reset();
  };

  const handlePlaceChanged = event => {
    const addressData = event?.reduce((accumulator, item) => {
      const type = item.types[0];

      if (type === "sublocality_level_1") {
        return { ...accumulator, sublocality: item.long_name };
      }

      return { ...accumulator, [type]: item.long_name };
    }, {});

    setValue(FACILITIES_CONFIG.street.key, addressData.route);
    setValue(FACILITIES_CONFIG.extNumber.key, addressData.street_number);
    setValue(FACILITIES_CONFIG.postalCode.key, addressData.postal_code);
    setValue(FACILITIES_CONFIG.colony.key, addressData.sublocality);
    setValue(FACILITIES_CONFIG.municipality.key, addressData.locality);
    setValue(
      FACILITIES_CONFIG.state.key,
      addressData.administrative_area_level_1
    );
  };

  const onSubmit = React.useCallback(
    formData => {
      const body = {
        registrationDate: dayjs(
          formData[FACILITIES_CONFIG.registrationDate.key]
        ).format("YYYY-MM-DD"),
        name: formData[FACILITIES_CONFIG.facilityName.key],
        contact: formData[FACILITIES_CONFIG.contactName.key],
        contactPhone: formData[FACILITIES_CONFIG.contactNumber.key],
        segment: formData[FACILITIES_CONFIG.regimen.key],

        address: {
          street: formData[FACILITIES_CONFIG.street.key],
          extNumber: formData[FACILITIES_CONFIG.extNumber.key],
          postalCode: formData[FACILITIES_CONFIG.postalCode.key],
          colony: formData[FACILITIES_CONFIG.colony.key],
          reference: formData[FACILITIES_CONFIG.reference.key],
          latitude: location.latitude,
          longitude: location.longitude
        },
        idRoute: formData[FACILITIES_CONFIG.route.key].id,
        idStation: stationSelected.id,
        idCollectPeriod: formData[FACILITIES_CONFIG.paymentCycle.key].id,
        notes: formData[FACILITIES_CONFIG.notes.key]
      };

      if (isAddMode) {
        dispatch(fetchNewFacility(body));
      } else {
        dispatch(fetchEditFacility(body, editFacility.id));
      }

      handleCloseForm();
    },
    [reset, isAddMode, editFacility, location]
  );

  useEffect(() => {
    if (editFacility) {
      const editRoute = routeList.find(
        route => route.id === editFacility.idRoute
      );
      const editDate = dayjs(
        editFacility[FACILITIES_CONFIG.registrationDate.key]
      ).format("YYYY-MM-DD");

      setValue(FACILITIES_CONFIG.registrationDate.key, editDate);
      setValue(
        FACILITIES_CONFIG.facilityName.key,
        editFacility[FACILITIES_CONFIG.facilityName.key]
      );
      setValue(
        FACILITIES_CONFIG.contactName.key,
        editFacility[FACILITIES_CONFIG.contactName.key]
      );
      setValue(
        FACILITIES_CONFIG.contactNumber.key,
        editFacility[FACILITIES_CONFIG.contactNumber.key]
      );
      setValue(
        FACILITIES_CONFIG.regimen.key,
        editFacility[FACILITIES_CONFIG.regimen.key]
      );
      setValue(FACILITIES_CONFIG.route.key, editRoute);
      setValue(FACILITIES_CONFIG.paymentCycle.key, editCollectPeriod);
      setValue(
        FACILITIES_CONFIG.notes.key,
        editFacility[FACILITIES_CONFIG.notes.key]
      );

      setValue(
        FACILITIES_CONFIG.street.key,
        editFacility.address[FACILITIES_CONFIG.street.key]
      );
      setValue(
        FACILITIES_CONFIG.extNumber.key,
        editFacility.address[FACILITIES_CONFIG.extNumber.key]
      );
      setValue(
        FACILITIES_CONFIG.postalCode.key,
        editFacility.address[FACILITIES_CONFIG.postalCode.key]
      );
      setValue(
        FACILITIES_CONFIG.colony.key,
        editFacility.address[FACILITIES_CONFIG.colony.key]
      );
      setValue(
        FACILITIES_CONFIG.municipality.key,
        editFacility.address[FACILITIES_CONFIG.municipality.key]
      );
      setValue(
        FACILITIES_CONFIG.state.key,
        editFacility.address[FACILITIES_CONFIG.state.key]
      );
      setValue(
        FACILITIES_CONFIG.reference.key,
        editFacility.address[FACILITIES_CONFIG.reference.key]
      );

      setLocation({
        latitude: editFacility.address.latitude,
        longitude: editFacility.address.longitude
      });
    }
  }, [editFacility]);

  useEffect(() => {
    if (isAddMode) {
      reset();
    }
  }, [isAddMode]);

  return (
    <>
      <Typography component="h2">
        {isAddMode
          ? FACILITIES_PHRASE.modal.titles.add
          : deleteMode
            ? FACILITIES_PHRASE.modal.titles.delete
            : FACILITIES_PHRASE.modal.titles.edit}
      </Typography>
      <StyledForm component="form" fullWidth onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          type="date"
          label={FACILITIES_CONFIG.registrationDate.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.registrationDate.key]}
          helperText={errors[FACILITIES_CONFIG.registrationDate.key]?.message}
          {...register(FACILITIES_CONFIG.registrationDate.key)}
          InputLabelProps={{
            shrink: true
          }}
        />
        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.facilityName.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.facilityName.key]}
          helperText={errors[FACILITIES_CONFIG.facilityName.key]?.message}
          {...register(FACILITIES_CONFIG.facilityName.key)}
          InputLabelProps={{
            shrink: true
          }}
        />
        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.contactName.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.contactName.key]}
          helperText={errors[FACILITIES_CONFIG.contactName.key]?.message}
          {...register(FACILITIES_CONFIG.contactName.key)}
          InputLabelProps={{
            shrink: true
          }}
        />
        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.contactNumber.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.contactNumber.key]}
          helperText={errors[FACILITIES_CONFIG.contactNumber.key]?.message}
          {...register(FACILITIES_CONFIG.contactNumber.key)}
          InputLabelProps={{
            shrink: true
          }}
        />

        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.regimen.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.regimen.key]}
          helperText={errors[FACILITIES_CONFIG.regimen.key]?.message}
          {...register(FACILITIES_CONFIG.regimen.key)}
          InputLabelProps={{
            shrink: true
          }}
        />

        <AutocompleteControlled
          name={FACILITIES_CONFIG.route.key}
          label={FACILITIES_CONFIG.route.label}
          options={routeList}
          control={control}
        />

        <FormControl fullWidth>
          <MapComponent
            onPlaceChanged={handlePlaceChanged}
            location={location}
            editedLocation={editFacility}
            onSetLocation={setLocation}
          />
        </FormControl>
        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.street.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.street.key]}
          helperText={errors[FACILITIES_CONFIG.street.key]?.message}
          {...register(FACILITIES_CONFIG.street.key)}
          InputLabelProps={{
            shrink: true
          }}
        />
        <FormRow>
          <TextField
            type="text"
            label={FACILITIES_CONFIG.extNumber.label}
            variant="outlined"
            error={!!errors[FACILITIES_CONFIG.extNumber.key]}
            helperText={errors[FACILITIES_CONFIG.extNumber.key]?.message}
            {...register(FACILITIES_CONFIG.extNumber.key)}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            type="text"
            label={FACILITIES_CONFIG.intNumber.label}
            variant="outlined"
            error={!!errors[FACILITIES_CONFIG.intNumber.key]}
            helperText={errors[FACILITIES_CONFIG.intNumber.key]?.message}
            {...register(FACILITIES_CONFIG.intNumber.key)}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            type="text"
            label={FACILITIES_CONFIG.postalCode.label}
            variant="outlined"
            error={!!errors[FACILITIES_CONFIG.postalCode.key]}
            helperText={errors[FACILITIES_CONFIG.postalCode.key]?.message}
            {...register(FACILITIES_CONFIG.postalCode.key)}
            InputLabelProps={{
              shrink: true
            }}
          />
        </FormRow>
        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.colony.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.colony.key]}
          helperText={errors[FACILITIES_CONFIG.colony.key]?.message}
          {...register(FACILITIES_CONFIG.colony.key)}
          InputLabelProps={{
            shrink: true
          }}
        />
        <FormRow>
          <TextField
            fullWidth
            type="text"
            label={FACILITIES_CONFIG.municipality.label}
            variant="outlined"
            error={!!errors[FACILITIES_CONFIG.municipality.key]}
            helperText={errors[FACILITIES_CONFIG.municipality.key]?.message}
            {...register(FACILITIES_CONFIG.municipality.key)}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            fullWidth
            type="text"
            label={FACILITIES_CONFIG.state.label}
            variant="outlined"
            error={!!errors[FACILITIES_CONFIG.state.key]}
            helperText={errors[FACILITIES_CONFIG.state.key]?.message}
            {...register(FACILITIES_CONFIG.state.key)}
            InputLabelProps={{
              shrink: true
            }}
          />
        </FormRow>
        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.reference.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.reference.key]}
          helperText={errors[FACILITIES_CONFIG.reference.key]?.message}
          {...register(FACILITIES_CONFIG.reference.key)}
          InputLabelProps={{
            shrink: true
          }}
        />
        <AutocompleteControlled
          name={FACILITIES_CONFIG.paymentCycle.key}
          label={FACILITIES_CONFIG.paymentCycle.label}
          options={collectPeriodList}
          control={control}
        />
        <TextField
          fullWidth
          type="text"
          label={FACILITIES_CONFIG.notes.label}
          variant="outlined"
          error={!!errors[FACILITIES_CONFIG.notes.key]}
          helperText={errors[FACILITIES_CONFIG.notes.key]?.message}
          {...register(FACILITIES_CONFIG.notes.key)}
          InputLabelProps={{
            shrink: true
          }}
        />

        <ButtonsWrapper>
          <RegularButton
            size="lg"
            variant="contained"
            color="danger"
            onClick={handleCloseForm}
          >
            {FACILITIES_PHRASE.modal.actions.cancel}
          </RegularButton>
          <RegularButton
            size="lg"
            variant="contained"
            type="submit"
            color="info"
          >
            {isAddMode
              ? FACILITIES_PHRASE.modal.actions.add
              : deleteMode
                ? FACILITIES_PHRASE.modal.actions.delete
                : FACILITIES_PHRASE.modal.actions.save}
          </RegularButton>
        </ButtonsWrapper>
      </StyledForm>
    </>
  );
});
